import React, { useEffect, useState } from 'react';
import { Table, TextInput, rem, createStyles, UnstyledButton, Group, Text, Center, Flex, ScrollArea, Button, Modal, useMantineTheme, Skeleton, Title, Image, Box, Portal, Overlay, Loader } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconChevronUp, IconChevronDown, IconSelector, IconSearch, IconCheck } from '@tabler/icons-react';
import { keys } from '@mantine/utils';
import PartnerForm from '../forms/PartnerForm';
import { getPartners, deletePartner } from '../../services/api.service';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import DeleteButton from '../buttons/DeleteButton';

// Custom styles
const useStyles = createStyles((theme) => ({
	container: {
		padding: theme.spacing.md,
		height: '100%',
	},
	th: {
		padding: '0 !important',
	},
	control: {
		width: '100%',
		padding: `${theme.spacing.xs} ${theme.spacing.md}`,
		'&:hover': {
			backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[1],
		},
	},
	icon: {
		width: rem(21),
		height: rem(21),
		borderRadius: rem(21),
	},
	imagePreview: {
		padding: 3,
		border: `1px solid ${theme.colorScheme === 'dark' ? theme.white : theme.black}`,
		borderRadius: theme.radius.sm,
		backgroundColor: theme.colorScheme === 'dark' ? theme.white : theme.black,
	}
}));

const Partners = () => {

	// State and hooks
	const [isLoading, setIsLoading] = useState(true);
	const [search, setSearch] = useState('');
	const [partners, setPartners] = useState([]);
	const [isEdit, setIsEdit] = useState(false);
	const [partnerEdit, setPartnerEdit] = useState({});
	const [sortedData, setSortedData] = useState([]);
	const [sortBy, setSortBy] = useState(null);
	const [reverseSortDirection, setReverseSortDirection] = useState(false);
	const [opened, { open, close }] = useDisclosure(false);
	const theme = useMantineTheme();
	const { classes } = useStyles();
	const [isDeleteLoading, toggleDeleteLoading] = useState(false);

	// On load
	useEffect(() => {
		getPartnersList();
		// eslint-disable-next-line
	}, []);

	// Get partners
	const getPartnersList = () => {

		// Get
		getPartners(function (response) {

			// Set partners and initial sorted data, remove loader
			setPartners(response.data);
			setSortedData(response.data);
			setIsLoading(false);

		}, function (error) {

			// Remove loader
			setIsLoading(false);

			// Show error
			notifications.show({
				title: 'Error',
				message: error,
				color: 'red'
			});

		});

	}

	// TH component
	const Th = ({ children, reversed, sorted, onSort }) => {
		const { classes } = useStyles();
		return (
			<th className={classes.th}>
				<UnstyledButton onClick={onSort} className={classes.control}>
					<Group position="apart">
						<Text fw={700} fz="sm">
							{children}
						</Text>
						<Center className={classes.icon}>
							{sorted ?
								(reversed ?
									<IconChevronUp size="0.9rem" stroke={1.5} />
									:
									<IconChevronDown size="0.9rem" stroke={1.5} />
								)
								: <IconSelector size="0.9rem" stroke={1.5} />}
						</Center>
					</Group>
				</UnstyledButton>
			</th>
		);
	}

	// Build table head
	const ths = (
		<tr>
			<Th
				sorted={sortBy === 'id'}
				reversed={reverseSortDirection}
				onSort={() => setSorting('id')}
			>
				ID
			</Th>
			<Th
				sorted={sortBy === 'active'}
				reversed={reverseSortDirection}
				onSort={() => setSorting('active')}
			>
				Activo
			</Th>
			<Th
				sorted={sortBy === 'logo'}
				reversed={reverseSortDirection}
				onSort={() => setSorting('logo')}
			>
				Logotipo
			</Th>
			<Th
				sorted={sortBy === 'name'}
				reversed={reverseSortDirection}
				onSort={() => setSorting('name')}
			>
				Nombre
			</Th>
			<Th
				sorted={sortBy === 'website'}
				reversed={reverseSortDirection}
				onSort={() => setSorting('website')}
			>
				Sitio Web
			</Th>
			<th className={classes.th}>
				<Text
					fw={700}
					fz="sm"
					className={classes.control}
					color="black"
				></Text>
			</th>
		</tr>
	);

	// Build rows
	const rows = sortedData.map((element) => (
		<tr
			style={{ cursor: 'pointer' }}
			key={element.id}
			onClick={(event) => togglePartnerEdit(event, element)}
		>
			<td width={100}>
				{element.id}
			</td>
			<td width={120}>
				{element.active ? 'Si' : 'No'}
			</td>
			<td width={150}>
				<Image
					src={element.logo}
					className={classes.imagePreview}
					radius={theme.radius.sm}
					maw={100}
				/>
			</td>
			<td>
				{element.name}
			</td>
			<td>
				{element.website}
			</td>
			<td>
				<DeleteButton
					onClick={(e) => {
						e.stopPropagation();
						openDeleteModal(element.id);
					}}
				/>
			</td>
		</tr>
	));

	// Handle search change
	const handleSearchChange = (event) => {
		const { value } = event.currentTarget;
		setSearch(value);
		setSortedData(sortData(partners, { sortBy, reversed: reverseSortDirection, search: value }));
	};

	// Get current sort
	const setSorting = (field) => {
		const reversed = field === sortBy ? !reverseSortDirection : false;
		setReverseSortDirection(reversed);
		setSortBy(field);
		setSortedData(sortData(partners, { sortBy: field, reversed, search }));
	};

	// Filter and return from query/search/sort
	const filterData = (data, search) => {
		const query = search.toLowerCase().trim();
		return data.filter((item) => keys(data[0]).some((key) => item[key].toString().toLowerCase().includes(query)));
	}

	// Sort data
	const sortData = (data, payload) => {

		// Assign
		const { sortBy } = payload;

		// No filter
		if (!sortBy) { return filterData(data, payload.search); }

		// Return filtered data
		return filterData(
			[...data].sort((a, b) => {
				if (payload.reversed) {
					return b[sortBy].toString().localeCompare(a[sortBy]);
				}

				return a[sortBy].toString().localeCompare(b[sortBy]);
			}),
			payload.search
		);

	}

	// Close partners modal
	const closeModal = () => {
		// Get partners and close modal
		getPartnersList();
		setIsEdit(false);
		setPartnerEdit({});
		close();
	}

	// Toggle partner edit
	const togglePartnerEdit = (event, partner) => {
		// Stop click and toggle edit
		event.stopPropagation();
		setIsEdit(true);
		setPartnerEdit(partner);
		// Open modal
		open();
	}

	// Open delete confirmation modal
	const openDeleteModal = (id) => 
		modals.openConfirmModal({
			title: 'Eliminar Partner',
			centered: true,
			children: (
				<Text size="sm">
					¿Estás seguro de querer borrar este partner? <strong>Se eliminarán las sucursales y beneficios asociados al mismo.</strong> Esta acción no se puede deshacer.
				</Text>
			),
			labels: { confirm: 'Eliminar partner', cancel: 'Cancelar' },
			confirmProps: { color: 'red', variant: 'light' },
			onCancel: () => {}, // Nothing
			onConfirm: () => triggerDeletePartner(id),
			closeOnClickOutside: false,
			zIndex: 400
		});

	// Trigger delete partner
	const triggerDeletePartner = (id) => {

		// Set loader
		toggleDeleteLoading(true);

		// Call API
		deletePartner(id, function () {

			// Show success
			notifications.show({
				title: 'Partner eliminado correctamente',
				color: 'green',
				icon: <IconCheck />
			});

			// Close after 1 second
			setTimeout(() => {

				// Remove preloader
				toggleDeleteLoading(false);

				// Close modal
				closeModal();

			}, 1000);

		}, function (error) {

			// Remove preloader
			toggleDeleteLoading(false);

			const getPersonalizedErrorMessage = (error) => {
				const msg = error?.message || 'Error desconocido';
			
				if (msg.includes("La entidad tiene relaciones con 'benefit")) {
					return 'El partner tiene un beneficio asociado';
				}

				return msg;
			};

			// Show error
			notifications.show({
				title: 'Error',
				message: getPersonalizedErrorMessage(error),
				color: 'red'
			});
		});
	}

	return (
		<>
			{/* Modal */}
			<Modal
				opened={opened}
				withCloseButton={false}
				centered
				size="xl"
				overlayProps={{
					color: theme.colorScheme === 'dark' ? theme.colors.dark[9] : theme.colors.gray[2],
					opacity: 0.55,
					blur: 3,
				}}
				closeOnClickOutside={false}
				closeOnEscape={false}
			>
				{/* New Partner Form */}
				<PartnerForm
					closeModal={closeModal}
					isEdit={isEdit}
					partner={partnerEdit}
				/>
			</Modal>
			{/* Delete Loader */}
			{isDeleteLoading ?
				<Portal>
					<Overlay
						sx={{ zIndex: 999 }}
						center
						blur={15}
					>
						<Loader size={100} />
					</Overlay>
				</Portal>
			: null}
			{/* Content */}
			<Box className="mainContainer">
				{/* Content */}
				<Flex
					direction="column"
					justify="space-between"
					className={classes.container}
				>
					{/* Title */}
					<Title mb="xl">
						Partners
					</Title>
					{/* Group */}
					<Flex direction="column">
						{/* Top */}
						<Flex mb="md">
							{/* New Partner */}
							<Button
								onClick={open}
								variant="filled"
							>
								Nuevo Partner
							</Button>
							{/* Search */}
							<TextInput
								placeholder="Buscar partners"
								icon={<IconSearch size="0.9rem" stroke={1.5} />}
								value={search}
								onChange={handleSearchChange}
								ml="sm"
								sx={{
									width: '100%',
									maxWidth: 350
								}}
								variant="filled"
							/>
						</Flex>
						{/* Table */}
						<ScrollArea>
							{isLoading ?
								<>
									<Skeleton height={40} radius="sm" />
									<Skeleton height={90} mt={12} radius="sm" />
									<Skeleton height={90} mt={12} radius="sm" />
									<Skeleton height={90} mt={12} radius="sm" />
									<Skeleton height={90} mt={12} radius="sm" />
								</>
								:
								<Table
									highlightOnHover
									sx={{ flex: 2 }}
								>
									<thead>{ths}</thead>
									<tbody>{rows.length > 0 ? (
										rows
									) : (
										<tr>
											<td colSpan={5}>
												<Text
													weight={500}
													align="center"
													my="xl"
													color="dimmed"
												>
													Sin resultados
												</Text>
											</td>
										</tr>
									)}</tbody>
								</Table>
							}
						</ScrollArea>
					</Flex>
				</Flex>
			</Box>
		</>
	);

}

export default Partners;
